import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import { ThemeProvider as EmotionReactThemeProvider } from '@emotion/react';
import { theme as defaultTheme } from '@lucid/theme';
import get from 'lodash/get';
import merge from 'lodash/merge';
import omit from 'lodash/omit';

import type { Lucid } from '../../types/types';

export interface ThemeProviderSiteThemeProps extends Lucid.Theme.Utility.Props {
  product: Lucid.Product.Sites;
}

export interface ThemeProviderProps extends ThemeProviderSiteThemeProps {
  children?: ReactNode | ReactNode[];
}

const getTheme = ({ product, theme }: ThemeProviderSiteThemeProps) => {
  const mutatedTheme = merge({}, theme, {
    accordion: {
      button: get(theme!.accordion!.button.products, product, theme!.accordion!.button),
      heading: get(theme!.accordion!.heading.products, product, theme!.accordion!.heading),
      item: get(theme!.accordion!.item.products, product, theme!.accordion!.item),
      panel: get(theme!.accordion!.panel.products, product, theme!.accordion!.panel),
    },
    buttonSize: get(theme!.buttonSize!.products, product, theme!.buttonSize),
    buttons: get(theme!.buttons!.products, product, theme!.buttons),
    cards: {
      card: get(theme!.cards!.card!.products, product, theme!.cards!.card),
      carousel: get(theme!.cards!.carousel!.products, product, theme!.cards!.carousel),
    },
    combobox: {
      box: get(theme!.combobox!.box!.products, product, theme!.combobox!.box),
      list: get(theme!.combobox!.list!.products, product, theme!.combobox!.list),
    },
    colors: get(theme!.colors!.products, product, theme!.colors),
    featureGrid: {
      container: get(theme!.featureGrid!.container.products, product, theme!.featureGrid.container),
      heading: get(theme!.featureGrid!.heading.products, product, theme!.featureGrid!.heading),
    },
    fontSizes: get(theme!.fontSizes!.products, product, theme!.fontSizes),
    headings: get(theme!.headings!.products, product, theme!.headings),
    hero: {
      multipleImage: get(theme!.hero!.multipleImage.products, product, theme!.hero.multipleImage),
      gridHero: get(theme!.hero!.gridHero.products, product, theme!.hero.gridHero),
    },
    link: get(theme!.link!.products, product, theme!.link),
    logoSection: {
      layout: get(theme!.logoSection!.layout!.products, product, theme!.logoSection!.layout),
      color: get(theme!.logoSection!.color!.products, product, theme!.logoSection!.color),
    },
    nav: {
      label: get(theme!.nav!.label.products, product, theme!.nav.label),
      logo: get(theme!.nav!.logo.products, product, theme!.nav.logo),
      textContent: get(theme!.nav!.textContent.products, product, theme!.nav.textContent),
    },
    paper: get(theme!.paper!.products, product, theme!.paper),
    peopleSection: get(theme!.peopleSection!.products, product, theme!.peopleSection),
    radii: get(theme!.radii!.products, product, theme!.radii),
    shadows: get(theme!.shadows!.products, product, theme!.shadows),
    typography: get(theme!.typography!.products, product, theme!.typography),
    video: {
      button: get(theme!.video!.button.products, product, theme!.video.button),
      carousel: get(theme!.video!.carousel.products, product, theme!.video.carousel),
      container: get(theme!.video!.container.products, product, theme!.video.container),
    },
    wave: get(theme!.wave!.products, product, theme!.wave),
  });

  const finalTheme = omit(mutatedTheme, [
    'accordion.button.products',
    'accordion.heading.products',
    'accordion.item.products',
    'accordion.panel.products',
    'buttonSize.products',
    'buttons.products',
    'cards.card.products',
    'cards.carousel.products',
    'combobox.boc.products',
    'combobox.list.products',
    'colors.products',
    'featureGrid.container.products',
    'featureGrid.heading.products',
    'fontSizes.products',
    'headings.products',
    'hero.multipleImage.products',
    'hero.gridHero.products',
    'link.products',
    'logoSection.layout.products',
    'logoSection.color.products',
    'nav.label.products',
    'nav.logo.products',
    'nav.textContent.products',
    'paper.products',
    'peopleSection.products',
    'radii.products',
    'shadows.products',
    'typography.products',
    'video.carousel.products',
    'video.player.products',
    'wave.products',
  ]);

  return finalTheme;
};

const ThemeProvider: FC<PropsWithChildren<ThemeProviderProps>> = ({ children, theme = defaultTheme, product }) => {
  if (!theme) throw new Error('Theme object not being passed to Theme Provider');

  const siteSpecificTheme = getTheme({ product, theme });

  return <EmotionReactThemeProvider theme={siteSpecificTheme || theme}>{children}</EmotionReactThemeProvider>;
};

export { ThemeProvider };
export default ThemeProvider;
